import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiGet, apiPut } from '../utils/api';

interface AppUser {
  id: string;
  email: string;
  name: string;
  telegramUserId: string | null;
  role: 'ADMIN' | 'MANAGER' | 'VIEWER' | string;
  isActive: boolean;
}

const SettingsUserEdit: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPasswordInput, setShowPasswordInput] = useState(false);

  const [form, setForm] = useState({
    name: '',
    email: '',
    telegramUserId: '',
    role: 'VIEWER',
    isActive: true,
    password: ''
  });

  const fetchUser = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      setError('');
      const data = await apiGet<{ user: AppUser }>(`/auth/users/${userId}`);
      const user = data?.user;
      if (!user) {
        setError('Пользователь не найден');
        return;
      }
      setForm({
        name: user.name || '',
        email: user.email || '',
        telegramUserId: user.telegramUserId || '',
        role: user.role || 'VIEWER',
        isActive: Boolean(user.isActive),
        password: ''
      });
    } catch (e: any) {
      setError(e?.message || 'Не удалось загрузить пользователя');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId) return;
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const payload: any = {
        name: form.name.trim(),
        email: form.email.trim(),
        telegramUserId: form.telegramUserId.trim(),
        role: form.role,
        isActive: form.isActive
      };
      if (form.password.trim()) {
        payload.password = form.password;
      }

      await apiPut(`/auth/users/${userId}`, payload);
      setSuccess('Изменения сохранены');
      setForm((prev) => ({ ...prev, password: '' }));
      setShowPasswordInput(false);
    } catch (e: any) {
      setError(e?.message || 'Не удалось сохранить изменения');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
        Загрузка пользователя...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">⚙️ Редактирование пользователя</h1>
        <button
          onClick={() => navigate('/settings/users')}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
        >
          Назад
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {success && (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          {success}
        </div>
      )}

      <form onSubmit={handleSave} className="bg-white rounded-lg shadow p-6 space-y-5 max-w-2xl">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Имя</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">TG ID</label>
          <input
            type="text"
            value={form.telegramUserId}
            onChange={(e) => setForm((prev) => ({ ...prev, telegramUserId: e.target.value }))}
            placeholder="Например: 123456789"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Роль</label>
            <select
              value={form.role}
              onChange={(e) => setForm((prev) => ({ ...prev, role: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="ADMIN">Администратор</option>
              <option value="MANAGER">Менеджер</option>
              <option value="VIEWER">Просмотр</option>
            </select>
          </div>

          <div className="flex items-end">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer pb-2">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span>Активен</span>
            </label>
          </div>
        </div>

        <div className="space-y-3">
          {!showPasswordInput ? (
            <button
              type="button"
              onClick={() => setShowPasswordInput(true)}
              className="px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100"
            >
              Изменить пароль
            </button>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Новый пароль</label>
              <div className="flex items-center gap-2">
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
                  placeholder="Введите новый пароль"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => {
                    setForm((prev) => ({ ...prev, password: '' }));
                    setShowPasswordInput(false);
                  }}
                  className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 whitespace-nowrap"
                >
                  Отмена
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">Минимум 6 символов</p>
            </div>
          )}
        </div>

        <div className="pt-2">
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Сохранение...' : 'Сохранить'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SettingsUserEdit;
